import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Chip,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  InputAdornment,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import baseURL from "../../config";

const badgeStyle = {
  m: 0.5,
  fontWeight: "bold",
};

const DepartureModal = ({ departureDetails = [] }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [approvalStatus, setApprovalStatus] = useState({}); // Store approval statuses for each contract
  const [openDialog, setOpenDialog] = useState(false);
  const [viewed, setViewed] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");

  // Filter data based on search term
  const filteredData = departureDetails.filter((item) => {
    const vesselName = item?.contract?.vesselName || "";
    const crewName = item?.contract?.crewName || "";
    return (
      vesselName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      crewName.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  // UseEffect to fetch approval status for each contractId when departureDetails change
  useEffect(() => {
    if (departureDetails.length > 0) {
      departureDetails.forEach((departure) => {
        const contractId = departure?.contract?._id;
        if (contractId) {
          checkApprovalStatus(contractId);
        }
      });
    }
  }, [departureDetails]); // Run effect when departureDetails change

  // Function to check the approval status of each contractId
  const checkApprovalStatus = async (contractId) => {
    try {
      const response = await axios.get(`${baseURL}/api/services/getApprovedSeaserviceByContractId/${contractId}`);
      setApprovalStatus((prevState) => ({
        ...prevState,
        [contractId]: response.data.approved, // Store approval status for each contractId
      }));
    } catch (error) {
      console.error("Error fetching approval status:", error);
    }
  };

  // Handle view PDF action
  const handleViewClick = async (crewingAgent, crewdetails) => {
    try {
      const response = await axios.get(
        `${baseURL}/api/agent/sea-service/${crewingAgent}/${crewdetails}`,
        {
          responseType: "blob",
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      setPdfUrl(url);
      setViewed(true);
    } catch (error) {
      console.error("Error fetching PDF:", error.message);
    }
  };

  // Handle approve action
  const handleApprove = async (contractId, crewingAgentId, crewId) => {
    try {
      const approvalResponse = await axios.post(`${baseURL}/api/services/approve-seaservice`, {
        contractId,
        crewingAgentId,
        crewId,
      });

      if (approvalResponse.status === 200) {
        alert("Sea Service approved successfully.");
        // Optionally refresh data here to reflect approval status
        checkApprovalStatus(contractId); // Refresh approval status after approving
      }
    } catch (error) {
      console.error("Approval failed:", error);
      alert("Failed to approve Sea Service. Please try again.");
    }
  };

  return (
    <Box sx={{ mt: 5, px: 2 }}>
      {/* Header */}
      <Paper
        elevation={3}
        sx={{
          p: 1,
          bgcolor: "#C30B120",
          color: "white",
          display: "flex",
          alignItems: "center",
        }}
      >
        <TextField
          placeholder="Search by Vessel Name or Crew"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="primary" />
              </InputAdornment>
            ),
          }}
          sx={{ bgcolor: "#f5f5f5", borderRadius: 2, width: "280px" }}
        />
        <Typography
          variant="h6"
          sx={{ mx: "auto", textAlign: "center", fontWeight: "bold", color: "#f5f5f5" }}
        >
          Arrival Details
        </Typography>
        <Box sx={{ width: 300 }} /> {/* Spacer */}
      </Paper>

      {/* Table */}
      <Paper>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>Sr. No.</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Contract Details</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Sea Service Request</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Approval</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  No matching departure data found.
                </TableCell>
              </TableRow>
            ) : (
              filteredData.map((item, index) => {
                const departureDate = item?.data?.date
                  ? new Date(item.data.date).toLocaleDateString("en-GB")
                  : "N/A";

                const crewId = item?.contract?.crewdetails?._id || item?.contract?.crewdetails;
                const crewingAgentId = item?.contract?.crewingAgent;

                const contractId = item?.contract?._id;
                const isApproved = approvalStatus[contractId]; // Get approval status for this contractId

                return (
                  <TableRow key={item._id || index}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      <Typography fontWeight="bold">
                        Vessel Name: {item?.contract?.vesselName || "N/A"}
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                        <Chip label={`Crew: ${item?.contract?.crewName || "N/A"}`} color="primary" sx={badgeStyle} />
                        <Chip label={`Indos No.: ${item?.contract?.crewdetails?.indosno || "N/A"}`} sx={{ ...badgeStyle, backgroundColor: "#ffeb3b", color: "#000" }} />
                        <Chip label={`Arrival Date: ${departureDate}`} sx={{ ...badgeStyle, backgroundColor: "#ffe0b2", color: "#e65100" }} />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Button variant="outlined" onClick={() => handleViewClick(crewingAgentId, crewId)}>
                        View
                      </Button>
                    </TableCell>
                    <TableCell>
                      {isApproved === undefined ? (
                        <Typography>Loading approval status...</Typography>
                      ) : isApproved ? (
                        <Typography>Approved</Typography>
                      ) : (
                        <Button
                          sx={{
                            backgroundColor: "green",
                            color: "white",
                            '&:hover': { backgroundColor: "darkgreen" },
                          }}
                          onClick={() =>
                            handleApprove(
                              item.contract?._id,
                              item.contract?.crewingAgent,
                              item.contract?.crewdetails?._id,
                            )
                          }
                        >
                          Approved
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Modal to show PDF */}
      <Dialog open={viewed} onClose={() => setViewed(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Sea Service Record</DialogTitle>
        <DialogContent sx={{ overflow: 'hidden', p: 0 }}>
          <Typography variant="h6">View Sea Service PDF</Typography>
          <embed
            src={pdfUrl}
            width="100%"
            height="500px"  // Set a fixed height or percentage height here
            type="application/pdf"
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default DepartureModal;
